import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { chatAPI } from 'services/api';
import { RootState } from 'store/rootReducer';

export type FunnelStageStatus = {
  id: string;
  name: string;
  createdAt: string;
};

export interface FunnelTag {
  id: string;
  name: string;
  color: string;
  description?: string;
}

export interface OpportunitySource {
  id: string;
  name: string;
}

export type FunnelStageCategoryType =
  | 'LEAD_ENTRY'
  | 'IN_PROGRESS'
  | 'WON'
  | 'LOST';

export interface FunnelStage {
  id: string;
  name: string;
  position: number;
  category: FunnelStageCategoryType;
}

export interface Funnel {
  id: string;
  name: string;
}

export interface OpportunityContact {
  id: string;
  name: string;
  contact: string;
}

export interface OpportunityStageStatus {
  id: string;
  name: string;
}

export interface FunnelResultReason {
  id: string;
  name: string;
  type: 'WON' | 'LOST';
}

export interface GetOpportunityDetailsResponse {
  id: string;
  code: string;
  description: string;
  businessName: string;
  documentType: 'CNPJ' | 'CPF';
  documentNumber: string;
  opportunityValue: number;
  createdAt: string;
  tags: FunnelTag[];
  contact: OpportunityContact;
  stageStatus: OpportunityStageStatus;
  stage: FunnelStage;
  source: OpportunitySource | null;
  createdBy: {
    id: string;
    type: 'USER' | 'BOT' | 'AGENT_IA';
    name: string;
  };
}

export type CreateFunnelStageOpportunityPayload = {
  funnelId: string;
  stageId: string;
  contactId: string;

  value: number | null;
  description: string | null;

  documentType: string | null;
  documentNumber: string | null;
  businessName: string | null;

  tagIds?: string[];

  sourceId: string | null;
  statusId: string | null;
};

export interface MoveOpportunitiesPayload {
  funnelId: string;
  opportunityIds: string[];
  toStageId: string;
  statusId: string | null;
}

export interface MoveOpportunities {
  funnelId: string;
  opportunityIds: string[];
}

export type MoveOpportunityToWonPayload = MoveOpportunities & {
  wonResultReasonId: string | null;
};

export type MoveOpportunityToLossPayload = MoveOpportunities & {
  lossResultReasonId: string | null;
};

export interface OpportunityHistoryActor {
  id: string;
  name: string;
  avatarUrl: string;
  type: 'USER' | 'BOT' | 'AGENT_IA';
}

export interface OpportunityHistoryStage {
  id: string;
  name: string;
}

export interface OpportunityHistory {
  id: string;
  enteredAt: string;
  actor: OpportunityHistoryActor;
  fromStage: OpportunityHistoryStage;
  toStage: OpportunityHistoryStage;
}

export type UpdateFunnelStageOpportunityPayload = {
  funnelId: string;
  stageId: string;
  opportunityId: string;

  value: number | null;
  description: string | null;
  documentType: string | null;
  documentNumber: string | null;
  businessName: string | null;
  sourceId: string | null;
};

type FunnelsState = {
  funnels: Funnel[];
  loadingFunnels: boolean;

  funnelStages: FunnelStage[];
  loadingFunnelStages: boolean;

  stageStatus: FunnelStageStatus[];
  loadingStageStatus: boolean;

  funnelTags: FunnelTag[];
  loadingFunnelTags: boolean;

  opportunitySources: OpportunitySource[];
  loadingOpportunitySources: boolean;

  opportunityDetails: GetOpportunityDetailsResponse | null;
  loadingOpportunityDetails: boolean;

  loadingMoveOpportunities: boolean;

  resultReasons: FunnelResultReason[];
  loadingResultReasons: boolean;

  opportunityHistory: OpportunityHistory[];
  loadingOpportunityHistory: boolean;
};

export const fetchFunnelsSelect = createAsyncThunk(
  'contatos/fetchFunnelsSelect',
  async () => {
    const response = await chatAPI.get('v2/sales-funnel/select');
    return response.data as Funnel[];
  },
);

export const fetchFunnelStageStatus = createAsyncThunk(
  'contatos/fetchFunnelStageStatus',
  async (params: { funnelId: string; stageId: string }) => {
    const { funnelId, stageId } = params;

    const response = await chatAPI.get(
      `v2/sales-funnel/${funnelId}/stages/${stageId}/status`,
    );

    return {
      data: response.data as FunnelStageStatus[],
    };
  },
);

export const fetchFunnelTags = createAsyncThunk(
  'contatos/fetchFunnelTags',
  async (params: { funnelId: string }) => {
    const { funnelId } = params;

    const response = await chatAPI.get(`v2/sales-funnel/${funnelId}/tags`);

    return {
      data: response.data as FunnelTag[],
    };
  },
);

export const fetchOpportunitySources = createAsyncThunk(
  'contatos/fetchOpportunitySources',
  async () => {
    const response = await chatAPI.get(`v2/sales-funnel/opportunity-sources`);

    return {
      data: response.data as OpportunitySource[],
    };
  },
);

export const createFunnelStageOpportunity = createAsyncThunk(
  'contatos/createFunnelStageOpportunity',
  async (payload: CreateFunnelStageOpportunityPayload) => {
    const { funnelId, stageId, ...rest } = payload;
    const response = await chatAPI.post(
      `/v2/sales-funnel/${funnelId}/stages/${stageId}/opportunities`,
      rest,
    );

    return response.data as FunnelStage[];
  },
);

export const fetchFunnelStagesSelect = createAsyncThunk(
  'contatos/fetchFunnelStagesSelect',
  async (params: { funnelId: string }) => {
    const { funnelId } = params;

    const response = await chatAPI.get(
      `/v2/sales-funnel/${funnelId}/stages/select`,
    );

    return {
      data: response.data as FunnelStage[],
    };
  },
);

export const moveOpportunities = createAsyncThunk(
  'contatos/moveOpportunities',
  async (payload: MoveOpportunitiesPayload, { rejectWithValue }) => {
    try {
      const { funnelId, ...rest } = payload;
      const response = await chatAPI.put(
        `/v2/sales-funnel/${funnelId}/opportunities/move`,
        rest,
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const fetchOpportunityDetails = createAsyncThunk(
  'contatos/fetchOpportunityDetails',
  async (params: { funnelId: string; opportunityId: string }) => {
    const { funnelId, opportunityId } = params;

    const response = await chatAPI.get<GetOpportunityDetailsResponse>(
      `/v2/sales-funnel/${funnelId}/opportunities/${opportunityId}`,
    );

    return response.data;
  },
);

export const fetchFunnelResultReasons = createAsyncThunk(
  'contatos/fetchFunnelResultReasons',
  async () => {
    const response = await chatAPI.get('/v2/sales-funnel/result-reasons');

    return response.data as FunnelResultReason[];
  },
);

export const moveOpportunityToWon = createAsyncThunk(
  'contatos/moveOpportunityToWon',
  async (payload: MoveOpportunityToWonPayload) => {
    const { funnelId, ...rest } = payload;
    const response = await chatAPI.put(
      `/v2/sales-funnel/${funnelId}/opportunities/move-to-won`,
      rest,
    );

    return response.data;
  },
);

export const moveOpportunityToLoss = createAsyncThunk(
  'contatos/moveOpportunityToLoss',
  async (payload: MoveOpportunityToLossPayload) => {
    const { funnelId, ...rest } = payload;
    const response = await chatAPI.put(
      `/v2/sales-funnel/${funnelId}/opportunities/move-to-loss`,
      rest,
    );

    return response.data;
  },
);

export const updateFunnelStageOpportunity = createAsyncThunk(
  'contatos/updateFunnelStageOpportunity',
  async (payload: UpdateFunnelStageOpportunityPayload) => {
    const { funnelId, stageId, opportunityId, ...rest } = payload;
    const response = await chatAPI.put(
      `/v2/sales-funnel/${funnelId}/stages/${stageId}/opportunities/${opportunityId}`,
      rest,
    );

    return response.data;
  },
);

export const fetchOpportunityHistory = createAsyncThunk(
  'contatos/fetchOpportunityHistory',
  async (params: { funnelId: string; opportunityId: string }) => {
    const { funnelId, opportunityId } = params;

    const response = await chatAPI.get<OpportunityHistory[]>(
      `/v2/sales-funnel/${funnelId}/opportunities/${opportunityId}/history`,
    );

    return response.data as OpportunityHistory[];
  },
);

export const addTagToFunnelOpportunity = createAsyncThunk(
  'contatos/addTagToFunnelOpportunity',
  async (
    params: {
      tagId: string;
      funnelId: string;
      stageId: string;
      opportunityId: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const { tagId, funnelId, stageId, opportunityId } = params;
      const response = await chatAPI.post(
        `/v2/sales-funnel/${funnelId}/stages/${stageId}/opportunities/${opportunityId}/tags`,
        { tagId },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const removeTagFromFunnelOpportunity = createAsyncThunk(
  'contatos/removeTagFromFunnelOpportunity',
  async (
    params: {
      tagId: string;
      funnelId: string;
      stageId: string;
      opportunityId: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const { tagId, funnelId, stageId, opportunityId } = params;
      const response = await chatAPI.delete(
        `/v2/sales-funnel/${funnelId}/stages/${stageId}/opportunities/${opportunityId}/tags/${tagId}`,
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// export async function removeTagFromFunnelOpportunity(params: RemoveTagFromFunnelOpportunityPayload) {
//   const { tagId, funnelId, stageId, opportunityId } = params;
//   const response = await chatApi.delete<Response<null>>(
//     `/v2/sales-funnel/${funnelId}/stages/${stageId}/opportunities/${opportunityId}/tags/${tagId}`
//   );

//   return response.data;
// }

const initialState: FunnelsState = {
  funnels: [],
  loadingFunnels: false,

  stageStatus: [],
  loadingStageStatus: false,

  funnelTags: [],
  loadingFunnelTags: false,

  opportunitySources: [],
  loadingOpportunitySources: false,

  funnelStages: [],
  loadingFunnelStages: false,

  opportunityDetails: null,
  loadingOpportunityDetails: false,

  loadingMoveOpportunities: false,

  resultReasons: [],
  loadingResultReasons: false,

  opportunityHistory: [],
  loadingOpportunityHistory: false,
};

export const funnelsSlice = createSlice({
  name: 'funnels',
  initialState,
  reducers: {},
  extraReducers(builder) {
    // funnel stage status
    builder.addCase(fetchFunnelStageStatus.pending, state => {
      state.loadingStageStatus = true;
    });
    builder.addCase(fetchFunnelStageStatus.fulfilled, (state, action) => {
      state.stageStatus = action.payload.data;
      state.loadingStageStatus = false;
    });
    builder.addCase(fetchFunnelStageStatus.rejected, state => {
      state.loadingStageStatus = false;
    });

    // funnel tags
    builder.addCase(fetchFunnelTags.pending, state => {
      state.loadingFunnelTags = true;
    });
    builder.addCase(fetchFunnelTags.fulfilled, (state, action) => {
      state.funnelTags = action.payload.data;
      state.loadingFunnelTags = false;
    });
    builder.addCase(fetchFunnelTags.rejected, state => {
      state.loadingFunnelTags = false;
    });

    // opportunity sources
    builder.addCase(fetchOpportunitySources.pending, state => {
      state.loadingOpportunitySources = true;
    });
    builder.addCase(fetchOpportunitySources.fulfilled, (state, action) => {
      state.opportunitySources = action.payload.data;
      state.loadingOpportunitySources = false;
    });
    builder.addCase(fetchOpportunitySources.rejected, state => {
      state.loadingOpportunitySources = false;
    });

    // funnel stages
    builder.addCase(fetchFunnelStagesSelect.pending, state => {
      state.loadingFunnelStages = true;
    });
    builder.addCase(fetchFunnelStagesSelect.fulfilled, (state, action) => {
      state.funnelStages = action.payload.data;
      state.loadingFunnelStages = false;
    });
    builder.addCase(fetchFunnelStagesSelect.rejected, state => {
      state.loadingFunnelStages = false;
    });

    // funnels
    builder.addCase(fetchFunnelsSelect.pending, state => {
      state.loadingFunnels = true;
    });
    builder.addCase(fetchFunnelsSelect.fulfilled, (state, action) => {
      state.funnels = action.payload;
      state.loadingFunnels = false;
    });
    builder.addCase(fetchFunnelsSelect.rejected, state => {
      state.loadingFunnels = false;
    });

    // opportunity details
    builder.addCase(fetchOpportunityDetails.pending, state => {
      state.loadingOpportunityDetails = true;
    });
    builder.addCase(fetchOpportunityDetails.fulfilled, (state, action) => {
      state.opportunityDetails = action.payload;
      state.loadingOpportunityDetails = false;
    });
    builder.addCase(fetchOpportunityDetails.rejected, state => {
      state.loadingOpportunityDetails = false;
    });

    // move opportunities
    builder.addCase(moveOpportunities.pending, state => {
      state.loadingMoveOpportunities = true;
    });
    builder.addCase(moveOpportunities.fulfilled, state => {
      state.loadingMoveOpportunities = false;
    });
    builder.addCase(moveOpportunities.rejected, state => {
      state.loadingMoveOpportunities = false;
    });

    // result reasons
    builder.addCase(fetchFunnelResultReasons.pending, state => {
      state.loadingResultReasons = true;
    });
    builder.addCase(fetchFunnelResultReasons.fulfilled, (state, action) => {
      state.resultReasons = action.payload;
      state.loadingResultReasons = false;
    });
    builder.addCase(fetchFunnelResultReasons.rejected, state => {
      state.loadingResultReasons = false;
    });

    // histórico de oportunidades
    builder.addCase(fetchOpportunityHistory.pending, state => {
      state.loadingOpportunityHistory = true;
    });
    builder.addCase(fetchOpportunityHistory.fulfilled, (state, action) => {
      state.opportunityHistory = action.payload;
      state.loadingOpportunityHistory = false;
    });
    builder.addCase(fetchOpportunityHistory.rejected, state => {
      state.loadingOpportunityHistory = false;
    });
  },
});

export const funnelsSelector = (state: RootState) => state.funnels;

export default funnelsSlice.reducer;
